import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { motion } from "framer-motion";

export const ChatContainer = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  background: rgba(255, 255, 255, 1);
  margin: -24px;
  border-radius: 0;
  overflow: hidden;
  border: none;
  margin-top: 0px;
`;

export const Sidebar = styled.div`
  width: 320px;
  background: rgba(255, 255, 255, 0.5);
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

export const ConversationItem = styled.div<{ active: boolean }>`
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.active ? "rgba(255, 49, 53, 0.1)" : "transparent"};
  border-left: 4px solid
    ${(props) => (props.active ? "#ff3135" : "transparent")};

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

export const MessageAreaContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 0; /* Crucial for scrolling children */
  height: 100%;
`;

export const MessagesList = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fa;
`;

export const MessageBubble = styled.div<{
  isSelf: boolean;
  isMediaOnly?: boolean;
}>`
  max-width: 70%;
  padding: ${(props) => (props.isMediaOnly ? "0" : "10px 15px")};
  border-radius: ${(props) =>
    props.isSelf ? "18px 18px 2px 18px" : "18px 18px 18px 2px"};
  background: ${(props) =>
    props.isMediaOnly ? "transparent" : props.isSelf ? "#ff3135" : "white"};
  color: ${(props) => (props.isSelf ? "white" : "#333")};
  align-self: ${(props) => (props.isSelf ? "flex-end" : "flex-start")};
  box-shadow: ${(props) =>
    props.isMediaOnly ? "none" : "0 2px 5px rgba(0, 0, 0, 0.05)"};
  position: relative;
  font-size: 14px;
  line-height: 1.4;
  overflow: hidden;
  min-width: ${(props) => (props.isMediaOnly ? "200px" : "unset")};
`;

export const InputContainer = styled.div`
  padding: 15px 20px;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StyledInput = styled.input`
  flex: 1;
  padding: 12px 18px;
  border-radius: 25px;
  border: 1px solid #eee;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #ff3135;
  }
`;

export const Avatar = styled.div<{ src?: string | null }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(props) =>
    props.src && !props.src.endsWith("undefined") && !props.src.endsWith("null")
      ? `url(${props.src})`
      : "#ff3135"};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
`;

export const Badge = styled.span<{ role: string }>`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  text-transform: uppercase;
  font-weight: bold;
  background: ${(props) => {
    switch (props.role) {
      case "admin":
        return "#eee";
      case "store":
        return "#e3f2fd";
      case "customer":
        return "#e8f5e9";
      default:
        return "#f5f5f5";
    }
  }};
  color: ${(props) => {
    switch (props.role) {
      case "admin":
        return "#666";
      case "store":
        return "#1976d2";
      case "customer":
        return "#2e7d32";
      default:
        return "#999";
    }
  }};
`;

export const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
`;

export const ModalContainer = styled(motion.div)`
  position: relative;
  width: unset;
  height: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  max-width: 95vw;
  max-height: 95vh;

  img,
  video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  }
`;

export const CloseButton = styled.div`
  position: fixed;
  top: 30px;
  right: 30px;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10000;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;
export const AttachmentPreview = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 20px;
  background: #f8f9fa;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  overflow-x: auto;
  max-height: 120px;
`;

export const AttachmentItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-placeholder {
    width: 100%;
    height: 100%;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }
`;

export const RemoveButton = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 14px;

  &:hover {
    background: rgba(255, 49, 53, 0.8);
  }
`;

export const OrderSidebar = styled.div`
  width: 300px;
  background: white;
  border-left: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 20px;
  gap: 20px;

  @media (max-width: 1200px) {
    display: none; // Hide on smaller screens or make toggleable
  }
`;

export const OrderSidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
`;

export const OrderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  h5 {
    margin: 0;
    font-size: 12px;
    text-transform: uppercase;
    color: #999;
    letter-spacing: 0.5px;
  }
`;

export const OrderItemRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 0;

  img {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
  }

  .details {
    flex: 1;
    min-width: 0;

    .name {
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .price {
      font-size: 11px;
      color: #666;
    }
  }
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;

  .label {
    color: #666;
  }

  .value {
    font-weight: 500;
  }
`;

const bounce = keyframes`
  0%, 80%, 100% { 
    transform: translateY(0);
  }
  40% { 
    transform: translateY(-5px);
  }
`;

export const SystemMessage = styled.div<{ isCall?: boolean; isSelf?: boolean }>`
  align-self: ${(props) =>
    props.isCall ? (props.isSelf ? "flex-end" : "flex-start") : "center"};
  margin: ${(props) => (props.isCall ? "5px 0" : "20px 0")};
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  border-radius: 24px;
  font-size: 11px;
  color: #555;
  text-align: center;
  max-width: 85%;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-left: 3px solid #ff4d4f; /* Accent color for highlight */

  &.call-ended {
    border-left-color: #52c41a;
  }

  &.call-started {
    border-left-color: #1890ff;
  }

  span {
    white-space: nowrap;
  }

  .time {
    font-size: 10px;
    opacity: 0.5;
    margin-left: 4px;
    font-weight: 400;
  }

  .call-duration {
    background: #e6f7ff;
    color: #1890ff;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 700;
    margin-left: 8px;
    border: 1px solid rgba(24, 144, 255, 0.2);

    &.ended {
      background: #f6ffed;
      color: #52c41a;
      border-color: rgba(82, 196, 26, 0.2);
    }
  }
`;

export const UnreadSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #ff4d4f;
    opacity: 0.2;
    z-index: 0;
  }

  span {
    position: relative;
    z-index: 1;
    background: #fff1f0;
    color: #ff4d4f;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: 1px solid rgba(255, 77, 79, 0.1);
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: white;
  border-radius: 18px 18px 18px 2px;
  width: fit-content;
  align-self: flex-start;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  margin-left: 20px;
  margin-bottom: 15px;

  span {
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: #999;
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out both;
  }

  span:nth-of-type(1) {
    animation-delay: -0.32s;
  }
  span:nth-of-type(2) {
    animation-delay: -0.16s;
  }
  span:nth-of-type(3) {
    animation-delay: 0s;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 20px 10px;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  h2 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 700;
  }
`;

export const SidebarSearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  svg {
    position: absolute;
    left: 12px;
    color: #94a3b8;
  }

  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 13px;
    color: #1e293b;
    transition: all 0.2s;

    &:focus {
      outline: none;
      background: white;
      border-color: #ff3135;
      box-shadow: 0 0 0 3px rgba(255, 49, 53, 0.1);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }
`;

export const NewChatButton = styled.div`
  width: 34px;
  height: 34px;
  background: #ff3135;
  color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(255, 49, 53, 0.2);

  &:hover {
    transform: scale(1.05) translateY(-1px);
    background: #e02d31;
    box-shadow: 0 6px 15px rgba(255, 49, 53, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  padding: 10px 15px;
  gap: 8px;
  overflow-x: auto;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: white;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FilterTab = styled.div<{ active: boolean }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.active ? "#ff3135" : "rgba(0, 0, 0, 0.04)")};
  color: ${(props) => (props.active ? "white" : "#666")};

  &:hover {
    background: ${(props) =>
      props.active ? "#ff3135" : "rgba(0, 0, 0, 0.08)"};
  }
`;

export const UserSearchModal = styled(motion.div)`
  background: white;
  width: 500px;
  max-width: 90vw;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 25px 70px -12px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export const SearchHeader = styled.div`
  padding: 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  h3 {
    margin: 0 0 16px 0;
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
  }
`;

export const RoleSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

export const RoleTab = styled.div<{ active: boolean }>`
  flex: 1;
  padding: 12px;
  text-align: center;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: ${(props) =>
    props.active ? "#ff3135" : "white"};
  color: ${(props) => (props.active ? "white" : "#64748b")};
  box-shadow: ${(props) => (props.active ? "0 4px 12px rgba(255, 49, 53, 0.25)" : "none")};
  border: 1px solid ${(props) => (props.active ? "#ff3135" : "#e2e8f0")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${(props) =>
      props.active ? "#e02d31" : "#f1f5f9"};
    transform: ${(props) => (props.active ? "none" : "translateY(-1px)")};
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 16px;
    color: #94a3b8;
  }

  input {
    width: 100%;
    padding: 12px 12px 12px 48px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: #ff3135;
      box-shadow: 0 0 0 4px rgba(255, 49, 53, 0.1);
    }
  }
`;

export const UserList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: white;
`;

export const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 4px;

  &:hover {
    background: #f8fafc;
    transform: translateX(4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  }

  &:active {
    transform: scale(0.98) translateX(4px);
  }

  .info {
    flex: 1;
    .name {
      font-weight: 700;
      font-size: 14px;
      color: #1e293b;
    }
    .role {
      font-size: 11px;
      color: #64748b;
      text-transform: uppercase;
      font-weight: 600;
    }
  }
`;
